import { useTranslation } from '@gimlite/common';
import { useStoreUIV1, Wizard, WizardItemType } from '@gimlite/ui-v1';
import { observer } from 'mobx-react-lite';
import {
  AntiPassBackMode,
  ArticleCode,
  PoolContractEntity,
} from '../../client/graphql';
import { MasterContractStep } from '../../common/steps/masterContract/masterContract.step';
import { portal } from '../../config';
import { ContractBaseFormType } from '../../form/contract-create/contract-create.form';

import { CredentialType } from '../../common/mapper/credential.mapper';
import { ConfirmationStep } from './steps/confirmation/confirmation.step';
import { ContractStep } from './steps/contract/contract.step';
import { InformationsStep } from './steps/informations/informations.step';
import { SummaryStep } from './steps/summary/summary.step';

export type CreateNewSubPoolContractContext = {
  parkingId: string | null;
  masterPoolContractId: string | null;
  product: {
    id: string | null;
    credentials: CredentialType.GlobalName[];
    articleCodes: Array<ArticleCode>;
  };
  parentContract: ContractBaseFormType.Data.ContractBase | null;
  contractBase: ContractBaseFormType.Data.ContractBase;
  informations: {
    name1: string | null;
    name2: string | null;
  };
  masterPoolExistedSave: PoolContractEntity[];
  mode: typeof portal;
  skipSubContract?: (skip: boolean) => void;
};

export const CreateNewSubPoolContractWizard = observer(() => {
  const { t, lang } = useTranslation();

  const { WizardStore } = useStoreUIV1();
  const { parkingId, mode } = WizardStore?.selected?.store;
  return (
    <Wizard<CreateNewSubPoolContractContext>
      config={{
        title: t(`createNewSubPoolContract`),
      }}
      data={{
        defaultContext: {
          parkingId,
          product: {
            id: null,
            credentials: [],
            articleCodes: [],
          },
          masterPoolContractId: null,
          parentContract: null,
          contractBase: {
            startDate: new Date().toISOString(),
            endDate: null,
            APBMode: AntiPassBackMode.Hard,
            APBNext: false,
            reference: null,
            description: null,
            state: true,
            capacity: null,
            articleCode: null,
          },
          informations: {
            name1: null,
            name2: null,
          },
          masterPoolExistedSave: [],
          mode,
        },
      }}
      handleEvent={{
        done: async (data) => {
          console.log('done', { data });
        },
        clear: () => {
          console.log('CLEAR');
        },
        close: () => {
          WizardStore.clear();
        },
      }}
    >
      <Wizard.Item
        config={{
          title: t('masterContract'),
          component: (
            params: WizardItemType.Config.Component<CreateNewSubPoolContractContext>,
          ) =>
            MasterContractStep({
              ...params,
              withRuleTenantCanCreateSubContract: true,
              ignoreDefaultSub: true,
            }),
        }}
      />
      <Wizard.Item config={{ title: t('contract'), component: ContractStep }} />
      <Wizard.Item
        config={{ title: t('informations'), component: InformationsStep }}
      />
      <Wizard.Item
        config={{ title: t('confirmation'), component: ConfirmationStep }}
      />
      <Wizard.Item config={{ title: t('summary'), component: SummaryStep }} />
    </Wizard>
  );
});
